<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <!-- Name -->
    <div>
      <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Name <span class="text-red-500">*</span>
      </label>
      <input
        id="name"
        v-model="formData.name"
        type="text"
        required
        :class="[
          'w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors',
          errors.name ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
        ]"
        placeholder="Ihr Name"
        @input="clearErrorIfValid('name')"
        @blur="validateField('name')"
      />
      <p v-if="errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.name }}</p>
    </div>

    <!-- E-Mail -->
    <div>
      <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        E-Mail <span class="text-red-500">*</span>
      </label>
      <input
        id="email"
        v-model="formData.email"
        type="email"
        required
        :class="[
          'w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors',
          errors.email ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
        ]"
        placeholder="ihre@email.de"
        @input="clearErrorIfValid('email')"
        @blur="validateField('email')"
      />
      <p v-if="errors.email" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.email }}</p>
    </div>

    <!-- Nachricht -->
    <div>
      <label for="message" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Nachricht <span class="text-red-500">*</span>
        <span class="text-xs text-gray-500 dark:text-gray-400 font-normal ml-2">
          ({{ formData.message.length }} / 2000 Zeichen)
        </span>
      </label>
      <textarea
        id="message"
        v-model="formData.message"
        required
        rows="6"
        maxlength="2000"
        :class="[
          'w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors resize-none',
          errors.message ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
        ]"
        placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
        @input="clearErrorIfValid('message')"
        @blur="validateField('message')"
      ></textarea>
      <p v-if="errors.message" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.message }}</p>
    </div>

    <!-- Honeypot-Feld (versteckt für Spam-Schutz) -->
    <div style="position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden;" aria-hidden="true">
      <label for="website">Website (nicht ausfüllen)</label>
      <input
        id="website"
        v-model="formData.honeypot"
        type="text"
        tabindex="-1"
        autocomplete="off"
      />
    </div>

    <!-- Datenschutz-Checkbox -->
    <div>
      <label class="flex items-start">
        <input
          v-model="formData.privacyAccepted"
          type="checkbox"
          required
          class="checkbox-custom mt-1 mr-3 h-4 w-4 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-0"
        />
        <span class="text-sm text-gray-700 dark:text-gray-300">
          Ich stimme der Verarbeitung meiner Daten gemäß 
          <router-link to="/datenschutz" class="text-blue-600 dark:text-blue-400 hover:underline" target="_blank">Datenschutzerklärung</router-link> 
          zu. <span class="text-red-500">*</span>
        </span>
      </label>
      <p v-if="errors.privacyAccepted" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ errors.privacyAccepted }}</p>
      
      <!-- Hinweis zur lokalen Speicherung -->
      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-start gap-1">
        <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          Ihre Eingaben werden lokal in Ihrem Browser gespeichert, damit sie beim Wechseln der Seite erhalten bleiben. 
          Die Daten werden automatisch gelöscht, wenn Sie den Browser-Tab schließen.
          <button 
            type="button"
            @click="clearSavedData"
            class="text-blue-600 dark:text-blue-400 hover:underline ml-1 inline-flex items-center gap-1"
            title="Gespeicherte Formulardaten löschen"
          >
            <TrashIcon class="w-3 h-3" />
            Daten löschen
          </button>
        </span>
      </p>
    </div>

    <!-- Submit Button -->
    <button
      type="submit"
      :disabled="submitting"
      class="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-blue-500/50 dark:hover:shadow-blue-600/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span v-if="submitting" class="flex items-center justify-center gap-2">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Wird gesendet...
      </span>
      <span v-else>Nachricht senden</span>
    </button>

    <!-- Success Message -->
    <div
      v-if="successMessage"
      class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-200"
    >
      {{ successMessage }}
    </div>

    <!-- Error Message -->
    <div
      v-if="errorMessage"
      class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200"
    >
      {{ errorMessage }}
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { logger } from '@/utils/logger'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { TrashIcon } from '@heroicons/vue/24/outline'

const emit = defineEmits<{
  submit: [data: {
    name: string
    email: string
    message: string
    privacyAccepted: boolean
  }]
}>()

// Storage-Schlüssel für Formulardaten
const STORAGE_KEY = 'contact_form_draft'

const formData = ref({
  name: '',
  email: '',
  message: '',
  privacyAccepted: false,
  honeypot: '' // Spam-Schutz: Sollte leer bleiben
})

// Lade gespeicherte Daten beim Mounten
const loadSavedData = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      // Stelle alle Felder wieder her, außer honeypot und privacyAccepted
      formData.value = {
        name: parsed.name || '',
        email: parsed.email || '',
        message: parsed.message || '',
        privacyAccepted: false, // Datenschutz-Checkbox wird nicht gespeichert (DSGVO)
        honeypot: ''
      }
    }
  } catch (error) {
    logger.warn('Fehler beim Laden der gespeicherten Formulardaten', error)
  }
}

// Speichere Formulardaten (mit Debounce)
let saveTimeout: ReturnType<typeof setTimeout> | null = null
const saveFormData = () => {
  // Lösche vorherigen Timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }
  
  // Speichere nach 500ms Verzögerung (Debounce)
  saveTimeout = setTimeout(() => {
    try {
      // Speichere nur relevante Felder (ohne honeypot und privacyAccepted)
      const dataToSave = {
        name: formData.value.name,
        email: formData.value.email,
        message: formData.value.message
      }
      
      // Nur speichern, wenn mindestens ein Feld ausgefüllt ist
      const hasData = Object.values(dataToSave).some(value => value && value.trim() !== '')
      
      if (hasData) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
      } else {
        // Wenn alle Felder leer sind, lösche gespeicherte Daten
        sessionStorage.removeItem(STORAGE_KEY)
      }
    } catch (error) {
      logger.warn('Fehler beim Speichern der Formulardaten', error)
    }
  }, 500)
}

// Lösche gespeicherte Daten
const clearSavedData = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
    formData.value = {
      name: '',
      email: '',
      message: '',
      privacyAccepted: false,
      honeypot: ''
    }
    errors.value = {}
  } catch (error) {
    logger.warn('Fehler beim Löschen der gespeicherten Formulardaten', error)
  }
}

// Watch für automatisches Speichern bei Änderungen
watch(() => formData.value.name, saveFormData)
watch(() => formData.value.email, saveFormData)
watch(() => formData.value.message, saveFormData)

// Lade Daten beim Mounten
onMounted(() => {
  loadSavedData()
})

const { handleError } = useErrorHandler()
const errors = ref<Record<string, string>>({})
const submitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const clearErrorIfValid = (field: string) => {
  // Entferne Fehler, wenn das Feld jetzt gültig ist
  if (errors.value[field]) {
    switch (field) {
      case 'name':
        if (formData.value.name && formData.value.name.length >= 2) {
          delete errors.value[field]
        }
        break
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (formData.value.email && emailRegex.test(formData.value.email)) {
          delete errors.value[field]
        }
        break
      case 'message':
        if (formData.value.message && formData.value.message.length >= 10) {
          delete errors.value[field]
        }
        break
    }
  }
}

const validateField = (field: string) => {
  delete errors.value[field]

  switch (field) {
    case 'name':
      if (!formData.value.name || formData.value.name.length < 2) {
        errors.value.name = 'Bitte geben Sie einen gültigen Namen ein (mindestens 2 Zeichen)'
      }
      break
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!formData.value.email || !emailRegex.test(formData.value.email)) {
        errors.value.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
      }
      break
    case 'message':
      if (!formData.value.message || formData.value.message.trim().length < 10) {
        errors.value.message = 'Bitte geben Sie eine Nachricht ein (mindestens 10 Zeichen)'
      }
      break
    case 'privacyAccepted':
      if (!formData.value.privacyAccepted) {
        errors.value.privacyAccepted = 'Bitte akzeptieren Sie die Datenschutzerklärung'
      }
      break
  }
}

const validateForm = (): boolean => {
  errors.value = {}
  validateField('name')
  validateField('email')
  validateField('message')
  validateField('privacyAccepted')

  return Object.keys(errors.value).length === 0
}

// Rate Limiting: Prüfe, ob zu viele Submissions in kurzer Zeit
const checkRateLimit = (): boolean => {
  const RATE_LIMIT_KEY = 'contact_form_submissions'
  const MAX_SUBMISSIONS = 3 // Maximal 3 Submissions
  const TIME_WINDOW = 15 * 60 * 1000 // 15 Minuten in Millisekunden

  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY)
    const now = Date.now()

    if (!stored) {
      // Erste Submission
      localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify([now]))
      return true
    }

    const submissions: number[] = JSON.parse(stored)
    // Entferne alte Submissions außerhalb des Zeitfensters
    const recentSubmissions = submissions.filter(
      timestamp => now - timestamp < TIME_WINDOW
    )

    if (recentSubmissions.length >= MAX_SUBMISSIONS) {
      const minutesLeft = Math.ceil(
        (TIME_WINDOW - (now - recentSubmissions[0])) / 60000
      )
      errorMessage.value = `Zu viele Anfragen. Bitte versuchen Sie es in ${minutesLeft} Minute(n) erneut.`
      return false
    }

    // Füge aktuelle Submission hinzu
    recentSubmissions.push(now)
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions))
    return true
  } catch (error) {
    logger.error('Fehler beim Rate Limiting', error)
    // Bei Fehler erlauben wir die Submission (fail-open)
    return true
  }
}

const handleSubmit = async () => {
  // Honeypot-Check: Wenn das Feld ausgefüllt wurde, ist es Spam
  if (formData.value.honeypot) {
    logger.warn('Spam erkannt: Honeypot-Feld wurde ausgefüllt')
    // Simuliere Erfolg, um dem Spammer keine Bestätigung zu geben
    successMessage.value = 'Vielen Dank für Ihre Nachricht!'
    formData.value = {
      name: '',
      email: '',
      message: '',
      privacyAccepted: false,
      honeypot: ''
    }
    // Lösche auch gespeicherte Daten bei Spam
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      logger.warn('Fehler beim Löschen der gespeicherten Formulardaten bei Spam', error)
    }
    return
  }

  if (!validateForm()) {
    return
  }

  // Rate Limiting prüfen
  if (!checkRateLimit()) {
    return
  }

  try {
    submitting.value = true
    errorMessage.value = ''
    successMessage.value = ''

    emit('submit', {
      name: formData.value.name,
      email: formData.value.email,
      message: formData.value.message,
      privacyAccepted: formData.value.privacyAccepted
    })

    // Reset form und lösche gespeicherte Daten
    formData.value = {
      name: '',
      email: '',
      message: '',
      privacyAccepted: false,
      honeypot: ''
    }
    
    // Lösche gespeicherte Daten nach erfolgreichem Absenden
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      logger.warn('Fehler beim Löschen der gespeicherten Formulardaten nach Absenden', error)
    }

    successMessage.value = 'Vielen Dank für Ihre Nachricht! Ich werde mich schnellstmöglich bei Ihnen melden.'
  } catch (error) {
    const appError = await handleError(error, 'Fehler beim Absenden', true)
    errorMessage.value = appError.userMessage
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
/* Custom Checkbox Styling */
.checkbox-custom {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 1rem;
  height: 1rem;
  border: 2px solid rgb(209, 213, 219); /* gray-300 */
  border-radius: 0.25rem;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
}

.dark .checkbox-custom {
  border-color: rgb(107, 114, 128); /* gray-500 */
  background-color: rgb(31, 41, 55); /* gray-800 */
}

.checkbox-custom:hover {
  border-color: rgb(59, 130, 246); /* blue-500 */
}

.dark .checkbox-custom:hover {
  border-color: rgb(96, 165, 250); /* blue-400 */
}

.checkbox-custom:checked {
  background-color: rgb(37, 99, 235); /* blue-600 */
  border-color: rgb(37, 99, 235);
}

.dark .checkbox-custom:checked {
  background-color: rgb(96, 165, 250); /* blue-400 */
  border-color: rgb(96, 165, 250);
}

.checkbox-custom:checked::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
}

.dark .checkbox-custom:checked::after {
  border-color: rgb(17, 24, 39); /* gray-900 für besseren Kontrast */
}

.checkbox-custom:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.dark .checkbox-custom:focus {
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.3);
}

.checkbox-custom:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>


